import React, { useState, useEffect, useRef } from 'react';
import { fmtL } from '../../../utils/currency';
import {
    Box,
    Typography,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Slider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import TuneIcon from '@mui/icons-material/Tune';
import { searchStockName, getStockByTicker } from '../../../api/api';
import { getAllPortfolios, createPortfolio, addHoldingToPortfolio } from '../../../api/portfolio_api';
import axios from 'axios';
import { getStocksScreenerQuant } from '../../../api/stocks_api';

const base_url = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const sectors = [
    'Technology',
    'Consumer Cyclical',
    'Healthcare',
    'Consumer Defensive',
    'Financial Services',
    'Energy',
    'Communication Services',
    'Industrials',
    'Basic Materials',
    'Utilities',
    'Real Estate'
];

const grades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];

const filterCategories = [
    { name: 'VALUATION', key: 'valuation_grade', type: 'grade' },
    { name: 'GROWTH', key: 'growth_grade', type: 'grade' },
    { name: 'PROFITABILITY', key: 'financial_profitability_grade', type: 'grade' },
    { name: 'RISK', key: 'risk_grade', type: 'grade' },
    { name: 'MOMENTUM', key: 'momentum_grade', type: 'grade' },
];


const ETFsScreener = () => {
    const [ticker, setTicker] = useState('');
    const [selectedSector, setSelectedSector] = useState('');
    const [overallScore, setOverallScore] = useState(0);
    const [filters, setFilters] = useState(
        filterCategories.reduce((acc, cat) => ({ ...acc, [cat.key]: '' }), {})
    );
    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);

    // Search dropdown state
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);

    // Portfolio dialog state
    const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);
    const [portfolios, setPortfolios] = useState([]);
    const [creatingPortfolio, setCreatingPortfolio] = useState(false);
    const [newPortfolioName, setNewPortfolioName] = useState('');
    const [addingToPortfolio, setAddingToPortfolio] = useState(false);
    const [selectedPortfolioId, setSelectedPortfolioId] = useState("");

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    useEffect(() => {
        if (!ticker.trim()) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        const delay = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await searchStockName(ticker.trim());
                if (res.success && Array.isArray(res.data) && res.data.length > 0) {
                    setSearchResults(res.data);
                    setShowDropdown(true);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        }, 300);

        return () => clearTimeout(delay);
    }, [ticker]);

    // Fetch portfolios when dialog opens
    useEffect(() => {
        if (portfolioDialogOpen) {
            fetchPortfolios();
        }
    }, [portfolioDialogOpen]);

    const fetchPortfolios = async () => {
        const res = await getAllPortfolios();
        if (res?.success) {
            setPortfolios(res.data || []);
        }
    };

    const fetchStocksWithFilters = async () => {
        setScanning(true);
        try {
            const params = {};

            // Add sector filter
            if (selectedSector) {
                params.sector = selectedSector;
            }

            // Add overall score filter
            if (overallScore > 0) {
                params.overall_score = overallScore;
            }

            // Add grade filters
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params[key] = filters[key];
                }
            });

            console.log('Fetching stocks with filters:', params);
            const res = await getStocksScreenerQuant(params);

            if (res?.success && Array.isArray(res.data)) {
                console.log('Received stocks:', res.data.length);
                setStocks(res.data.map(stock => ({
                    ...stock,
                    added: false
                })));
            } else {
                console.warn('Invalid response:', res);
                setStocks([]);
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setStocks([]);
        } finally {
            setScanning(false);
        }
    };

    // Auto-fetch stocks when filters change
    useEffect(() => {
        const delay = setTimeout(() => {
            // Only call API if at least one filter is active
            const hasFilters = selectedSector || overallScore > 0 || Object.values(filters).some(v => v);
            if (hasFilters) {
                console.log('🔄 Auto-fetching due to filter change...');
                fetchStocksWithFilters();
            } else {
                console.log('🧹 Clearing stocks - no filters active');
                setStocks([]);
            }
        }, 500); // Debounce for 500ms

        return () => clearTimeout(delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSector, overallScore, JSON.stringify(filters)]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearAllFilters = () => {
        setFilters(filterCategories.reduce((acc, cat) => ({ ...acc, [cat.key]: '' }), {}));
        setSelectedSector('');
        setOverallScore(0);
    };

    const handleScanWithFilters = () => {
        // Trigger immediate fetch without waiting for debounce
        console.log('🚀 Manual scan triggered');
        fetchStocksWithFilters();
    };

    const handleScanSector = (sector) => {
        // Just update the sector, useEffect will handle the API call
        console.log('🏢 Sector selected:', sector);
        setSelectedSector(sector);
    };

    const handleAnalyze = async () => {
        if (!ticker.trim()) return;

        setLoading(true);
        try {
            const res = await getStockByTicker(ticker.trim().toUpperCase());
            if (res?.success) {
                const stockData = res.data;

                // Check if already in table
                const exists = stocks.find(s => s.ticker === stockData.ticker);
                if (exists) {
                    alert('Stock already in screener');
                    return;
                }

                // Add to table
                const newStock = {
                    ticker: stockData.ticker,
                    system_rating: 'HOLD',
                    overall_score: 0,
                    valuation_grade: '',
                    growth_grade: '',
                    financial_profitability_grade: '',
                    risk_grade: '',
                    momentum_grade: '',
                    added: false,
                };

                setStocks(prev => [newStock, ...prev]);
                setTicker('');
                setShowDropdown(false);
            }
        } catch (error) {
            console.error('Analyze error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectFromDropdown = (stock) => {
        setTicker(stock.ticker);
        setShowDropdown(false);
    };

    const handleAddToPortfolio = (stock) => {
        setSelectedStock(stock);
        setPortfolioDialogOpen(true);
    };

    const handleAddToExistingPortfolio = async (portfolioId) => {
        if (!selectedStock) return;

        setAddingToPortfolio(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            await addHoldingToPortfolio(portfolioId, {
                ticker: selectedStock.ticker,
                shares: 1,
                cost_basis: 100,
                purchase_date: today,
            });

            setStocks(prev =>
                prev.map(s => s.ticker === selectedStock.ticker ? { ...s, added: true } : s)
            );

            setPortfolioDialogOpen(false);
            setSelectedStock(null);
            setSelectedPortfolioId("");
        } catch (error) {
            console.error('Add to portfolio error:', error);
        } finally {
            setAddingToPortfolio(false);
        }
    };

    const handleCreatePortfolio = async () => {
        if (!newPortfolioName.trim() || !selectedStock) return;

        setAddingToPortfolio(true);
        try {
            const res = await createPortfolio({ name: newPortfolioName.trim() });
            if (res?.success) {
                const today = new Date().toISOString().split('T')[0];
                await addHoldingToPortfolio(res.data.id, {
                    ticker: selectedStock.ticker,
                    shares: 1,
                    cost_basis: 100,
                    purchase_date: today,
                });

                setStocks(prev =>
                    prev.map(s => s.ticker === selectedStock.ticker ? { ...s, added: true } : s)
                );

                setPortfolioDialogOpen(false);
                setSelectedStock(null);
                setNewPortfolioName('');
                setCreatingPortfolio(false);
            }
        } catch (error) {
            console.error('Create portfolio error:', error);
        } finally {
            setAddingToPortfolio(false);
        }
    };

    const getGradeColor = (grade) => {
        if (!grade) return '#64748b';
        const gradeValue = grades.indexOf(grade);
        if (gradeValue <= 2) return '#10b981'; // A+, A, A-
        if (gradeValue <= 5) return '#3b82f6'; // B+, B, B-
        if (gradeValue <= 8) return '#f59e0b'; // C+, C, C-
        return '#ef4444'; // D+, D, D-, F
    };

    const getScoreColor = (score) => {
        if (!score) return '#64748b';
        const numScore = parseFloat(score);
        if (numScore >= 80) return '#10b981'; // Excellent: 80-100
        if (numScore >= 60) return '#3b82f6'; // Good: 60-79
        if (numScore >= 40) return '#f59e0b'; // Fair: 40-59
        return '#ef4444'; // Poor: 0-39
    };

    const getRatingColor = (rating) => {
        if (rating === 'BUY' || rating === 'STRONG BUY') return '#10b981';
        if (rating === 'HOLD') return '#3b82f6';
        return '#ef4444';
    };

    const hasActiveFilters = selectedSector || overallScore > 0 || Object.values(filters).some(v => v);

    const calculateAlphaAvg = () => {
        if (stocks.length === 0) return 0;
        const validScores = stocks.filter(s => s.overall_score > 0);
        if (validScores.length === 0) return 0;
        const sum = validScores.reduce((acc, stock) => acc + parseFloat(stock.overall_score || 0), 0);
        return (sum / validScores.length).toFixed(1);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#0f172a",
                px: { xs: 2, md: 3 },
                py: 2.5,
            }}
        >
            {/* Header */}
            <Box mb={2.5}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                        <Typography
                            fontSize="var(--font-size-lg)"
                            fontWeight={600}
                            color="#e2e8f0"
                            mb={0.25}
                            letterSpacing="-0.01em"
                        >
                            ETFs Screener
                        </Typography>
                        <Typography fontSize="var(--font-size-xs2)" color="#64748b" fontWeight={400}>
                            Multi-factor S&P 500 screening engine powered by quantitative analysis.
                        </Typography>
                    </Box>

                    <Box display="flex" gap={1} ref={searchRef}>
                        <Box position="relative">
                        </Box>

                        {/* <Button
                            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon sx={{ fontSize: 14 }} />}
                            onClick={handleAnalyze}
                            disabled={loading}
                            sx={{
                                bgcolor: "#3b82f6",
                                color: "#fff",
                                fontSize: 11.5,
                                fontWeight: 600,
                                textTransform: "none",
                                px: 2,
                                py: 0.65,
                                borderRadius: 1,
                                "&:hover": {
                                    bgcolor: "#2563eb",
                                },
                                "&.Mui-disabled": {
                                    bgcolor: "#334155",
                                    color: "#64748b",
                                },
                            }}
                        >
                            Analyze
                        </Button> */}
                        <Button
                            startIcon={scanning ? <CircularProgress size={14} color="inherit" /> : <TuneIcon sx={{ fontSize: 14 }} />}
                            onClick={handleScanWithFilters}
                            disabled={scanning}
                            sx={{
                                bgcolor: "#1e293b",
                                color: "#10b981",
                                fontSize: 11.5,
                                fontWeight: 600,
                                textTransform: "none",
                                px: 2,
                                py: 0.65,
                                borderRadius: 1,
                                border: "1px solid #10b981",
                                "&:hover": {
                                    bgcolor: "#334155",
                                },
                                "&.Mui-disabled": {
                                    bgcolor: "#1e293b",
                                    color: "#475569",
                                    borderColor: "#334155",
                                },
                            }}
                        >
                            Scan with Filters
                        </Button>
                    </Box>
                </Box>

                {/* Sector Buttons */}
                <Box display="grid" gridTemplateColumns="repeat(6, 1fr)" gap={1} mb={2}>
                    {sectors.map((sector) => (
                        <Button
                            key={sector}
                            onClick={() => handleScanSector(sector)}
                            disabled={scanning}
                            sx={{
                                bgcolor: selectedSector === sector ? "#334155" : "#1e293b",
                                color: "#e2e8f0",
                                fontSize: 9.5,
                                fontWeight: 500,
                                textTransform: "none",
                                px: 1,
                                py: 1,
                                borderRadius: 1,
                                border: selectedSector === sector ? "1px solid #3b82f6" : "1px solid #334155",
                                flexDirection: "column",
                                gap: 0.5,
                                "&:hover": {
                                    bgcolor: "#334155",
                                    borderColor: "#3b82f6",
                                },
                                "&.Mui-disabled": {
                                    bgcolor: "#1e293b",
                                    color: "#475569",
                                    borderColor: "#334155",
                                },
                            }}
                        >
                            <Typography fontSize={8} color="#64748b" textTransform="uppercase" letterSpacing="0.03em">
                                Scan Sector
                            </Typography>
                            <Typography fontSize="var(--font-size-xs2)" fontWeight={600}>
                                {sector}
                            </Typography>
                        </Button>
                    ))}
                </Box>
            </Box>

            {/* Active Filter Criteria */}
            <Box
                sx={{
                    bgcolor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 1.5,
                    p: 2,
                    mb: 2,
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color="#e2e8f0" letterSpacing="0.05em" textTransform="none">
                            Active Filter Criteria
                        </Typography>
                        {hasActiveFilters && (
                            <Chip
                                label={`${[selectedSector ? 1 : 0, overallScore > 0 ? 1 : 0, ...Object.values(filters).map(v => v ? 1 : 0)].reduce((a, b) => a + b, 0)} active`}
                                size="small"
                                sx={{
                                    bgcolor: "#334155",
                                    color: "#3b82f6",
                                    fontSize: 9,
                                    height: 18,
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </Box>
                    <Button
                        startIcon={<ClearIcon sx={{ fontSize: 12 }} />}
                        onClick={clearAllFilters}
                        disabled={!hasActiveFilters}
                        sx={{
                            fontSize: 10,
                            fontWeight: 500,
                            color: hasActiveFilters ? "#64748b" : "#475569",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            minWidth: "auto",
                            p: 0,
                            "&:hover": {
                                bgcolor: "transparent",
                                color: "#3b82f6",
                            },
                        }}
                    >
                        Clear All
                    </Button>
                </Box>

                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={2}>
                    {/* Sector Filter */}
                    <Box>
                        <Typography fontSize={9} color="#64748b" fontWeight={600} letterSpacing="0.03em" textTransform="uppercase" mb={0.75}>
                            Sector
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={selectedSector}
                                displayEmpty
                                onChange={(e) => setSelectedSector(e.target.value)}
                                sx={{
                                    fontSize: 11,
                                    bgcolor: "#0f172a",
                                    color: "#e2e8f0",
                                    borderRadius: 1,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#334155",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#475569",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#3b82f6",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#94a3b8",
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: "#0f172a",
                                            border: "1px solid #334155",
                                            mt: 0.5,
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <Typography fontSize="var(--font-size-xs2)" color="#64748b">
                                        All Sectors
                                    </Typography>
                                </MenuItem>
                                {sectors.map((sector) => (
                                    <MenuItem
                                        key={sector}
                                        value={sector}
                                        sx={{
                                            fontSize: 11,
                                            color: "#e2e8f0",
                                            "&:hover": {
                                                bgcolor: "#334155",
                                            },
                                        }}
                                    >
                                        {sector}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Overall Score Filter */}
                    <Box gridColumn="span 2">
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography fontSize={9} color="#64748b" fontWeight={600} letterSpacing="0.03em" textTransform="uppercase">
                                Overall FA Score (0-100)
                            </Typography>
                            <Typography fontSize={9} color={overallScore > 0 ? "#3b82f6" : "#64748b"} fontWeight={600}>
                                Min: {fmtL(overallScore, 0)}
                            </Typography>
                        </Box>
                        <Slider
                            value={overallScore}
                            onChange={(e, value) => setOverallScore(value)}
                            min={0}
                            max={100}
                            step={1}
                            marks={[
                                { value: 0, label: '0' },
                                { value: 25, label: '25' },
                                { value: 50, label: '50' },
                                { value: 75, label: '75' },
                                { value: 100, label: '100' },
                            ]}
                            sx={{
                                color: '#3b82f6',
                                height: 3,
                                '& .MuiSlider-thumb': {
                                    width: 12,
                                    height: 12,
                                    '&:hover': {
                                        boxShadow: '0 0 0 6px rgba(59, 130, 246, 0.16)',
                                    },
                                },
                                '& .MuiSlider-track': {
                                    border: 'none',
                                },
                                '& .MuiSlider-rail': {
                                    opacity: 0.3,
                                    bgcolor: '#475569',
                                },
                                '& .MuiSlider-mark': {
                                    display: 'none',
                                },
                                '& .MuiSlider-markLabel': {
                                    fontSize: 8,
                                    color: '#64748b',
                                    top: 20,
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Grade Filters */}
                <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={2}>
                    {filterCategories.map((category) => (
                        <Box key={category.key}>
                            <Typography fontSize={9} color="#64748b" fontWeight={600} letterSpacing="0.03em" textTransform="uppercase" mb={0.75}>
                                {category.name}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={filters[category.key]}
                                    displayEmpty
                                    onChange={(e) => handleFilterChange(category.key, e.target.value)}
                                    sx={{
                                        fontSize: 11,
                                        bgcolor: "#0f172a",
                                        color: "#e2e8f0",
                                        borderRadius: 1,
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#334155",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#475569",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#3b82f6",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            color: "#94a3b8",
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: "#0f172a",
                                                border: "1px solid #334155",
                                                mt: 0.5,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <Typography fontSize="var(--font-size-xs2)" color="#64748b">
                                            Min Grade
                                        </Typography>
                                    </MenuItem>
                                    {grades.map((grade) => (
                                        <MenuItem
                                            key={grade}
                                            value={grade}
                                            sx={{
                                                fontSize: 11,
                                                color: "#e2e8f0",
                                                "&:hover": {
                                                    bgcolor: "#334155",
                                                },
                                            }}
                                        >
                                            {grade}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Results Table */}
            <Box
                sx={{
                    bgcolor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: "#1a2332" }}>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Ticker
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                System Rating
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Overall FA Score
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Valuation
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Growth
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Profitability
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Risk
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Momentum
                            </TableCell>
                            <TableCell sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #334155", py: 1 }}>
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scanning ? (
                            <TableRow>
                                <TableCell colSpan={9} sx={{ py: 6, textAlign: "center", borderBottom: "none" }}>
                                    <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
                                    <Typography fontSize="var(--font-size-xs2)" color="#64748b" mt={2}>
                                        Scanning stocks...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : stocks.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} sx={{ py: 6, textAlign: "center", borderBottom: "none" }}>
                                    <Typography fontSize="var(--font-size-xs2)" color="#64748b">
                                        {hasActiveFilters
                                            ? "No stocks match the current filter criteria"
                                            : "No stocks in screener. Apply filters to scan for stocks."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            stocks.map((stock) => (
                                <TableRow
                                    key={stock.ticker}
                                    sx={{
                                        "&:hover": { bgcolor: "#1a2332" },
                                        borderBottom: "1px solid #334155",
                                    }}
                                >
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-sm)" fontWeight={700} color="#3b82f6">
                                            {stock.ticker}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        {stock.system_rating && (
                                            <Chip
                                                label={stock.system_rating}
                                                size="small"
                                                sx={{
                                                    bgcolor: getRatingColor(stock.system_rating),
                                                    color: "#fff",
                                                    fontSize: 9,
                                                    height: 20,
                                                    fontWeight: 700,
                                                    letterSpacing: "0.03em",
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color={getScoreColor(stock.overall_score)}>
                                            {stock.overall_score ? Number(stock.overall_score).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color={getGradeColor(stock.valuation_grade)}>
                                            {stock.valuation_grade || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color={getGradeColor(stock.growth_grade)}>
                                            {stock.growth_grade || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color={getGradeColor(stock.financial_profitability_grade)}>
                                            {stock.financial_profitability_grade || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color={getGradeColor(stock.risk_grade)}>
                                            {stock.risk_grade || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color={getGradeColor(stock.momentum_grade)}>
                                            {stock.momentum_grade || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
                                        {stock.added ? (
                                            <Chip
                                                icon={<CheckIcon sx={{ fontSize: 11 }} />}
                                                label="ADDED"
                                                size="small"
                                                sx={{
                                                    bgcolor: "rgba(16, 185, 129, 0.1)",
                                                    color: "#10b981",
                                                    fontSize: 9,
                                                    height: 20,
                                                    fontWeight: 700,
                                                    letterSpacing: "0.03em",
                                                }}
                                            />
                                        ) : (
                                            <Button
                                                startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                                onClick={() => handleAddToPortfolio(stock)}
                                                sx={{
                                                    fontSize: 9,
                                                    fontWeight: 600,
                                                    color: "#3b82f6",
                                                    textTransform: "none",
                                                    minWidth: "auto",
                                                    px: 1,
                                                    py: 0.5,
                                                    "&:hover": {
                                                        bgcolor: "rgba(59, 130, 246, 0.1)",
                                                    },
                                                }}
                                            >
                                                Portfolio
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Box>

            {/* Index Correlation Analytics */}
            <Box
                sx={{
                    bgcolor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 1.5,
                    p: 2.5,
                }}
            >
                <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="#e2e8f0" mb={0.5}>
                    Index Correlation Analytics
                </Typography>
                <Typography fontSize={10.5} color="#64748b" mb={2.5} lineHeight={1.6} maxWidth={900}>
                    This engine evaluates components of the S&P 500 against institutional factor benchmarks.
                    All quantitative ratings are synthesized using advanced reasoning over real-time financial
                    fundamentals and momentum trends.
                </Typography>

                <Box display="flex" gap={6}>
                    <Box>
                        <Typography fontSize={9} color="#64748b" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em" mb={0.75}>
                            Total Screened
                        </Typography>
                        <Typography fontSize={32} fontWeight={700} color="#e2e8f0">
                            {stocks.length}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography fontSize={9} color="#64748b" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em" mb={0.75}>
                            Avg FA Score
                        </Typography>
                        <Typography fontSize={32} fontWeight={700} color="#10b981">
                            {calculateAlphaAvg()}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Add to Portfolio Dialog */}
            <Dialog
                open={portfolioDialogOpen}
                onClose={() => {
                    setPortfolioDialogOpen(false);
                    setSelectedStock(null);
                    setCreatingPortfolio(false);
                    setNewPortfolioName('');
                    setSelectedPortfolioId("");
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: "#1e293b",
                        backgroundImage: "none",
                        borderRadius: 1.5,
                        border: "1px solid #334155",
                    },
                }}
            >
                <DialogTitle sx={{ color: "#e2e8f0", borderBottom: "1px solid #334155", py: 1.75, px: 2.5 }}>
                    <Box display="flex" alignItems="center" gap={1.25}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: "#334155",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FolderIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        </Box>
                        <Typography fontSize="var(--font-size-sm)" fontWeight={600}>
                            Add {selectedStock?.ticker} to Portfolio
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 2.5, mt: 2 }}>
                    {!creatingPortfolio ? (
                        <>
                            <Typography fontSize="var(--font-size-xs2)" color="#94a3b8" mb={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                                Note: By default only one share will be allocated
                            </Typography>
                            <Typography fontSize="var(--font-size-xs2)" color="#94a3b8" mb={2}>
                                Select a portfolio
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <Select
                                    value={selectedPortfolioId}
                                    displayEmpty
                                    disabled={addingToPortfolio}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedPortfolioId(id);
                                        // handleAddToExistingPortfolio(id);
                                    }}
                                    sx={{
                                        fontSize: 12,
                                        bgcolor: "#0f172a",
                                        color: "#e2e8f0",
                                        borderRadius: 1.5,
                                        border: "1px solid #334155",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            color: "#94a3b8",
                                        },
                                        "&:hover": {
                                            bgcolor: "#020617",
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: "#020617",
                                                border: "1px solid #334155",
                                                mt: 1,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <Typography fontSize="var(--font-size-xs2)" color="#64748b">
                                            Select a portfolio
                                        </Typography>
                                    </MenuItem>

                                    {portfolios.map((portfolio) => (
                                        <MenuItem
                                            key={portfolio.id}
                                            value={portfolio.id}
                                            sx={{
                                                fontSize: 12,
                                                color: "#e2e8f0",
                                                "&:hover": {
                                                    bgcolor: "#334155",
                                                },
                                            }}
                                        >
                                            {portfolio.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box display="flex" alignItems="center" mb={2}>
                                <Box flex={1} height="1px" bgcolor="#334155" />
                                <Typography
                                    fontSize="var(--font-size-xs2)"
                                    color="#94a3b8"
                                    px={1.5}
                                    whiteSpace="nowrap"
                                >
                                    or
                                </Typography>
                                <Box flex={1} height="1px" bgcolor="#334155" />
                            </Box>

                            <Typography fontSize="var(--font-size-xs2)" color="#94a3b8" mb={2}>
                                Create a new portfolio and add
                            </Typography>
                            <Button
                                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setCreatingPortfolio(true)}
                                fullWidth
                                sx={{
                                    fontSize: 11.5,
                                    fontWeight: 500,
                                    color: "#3b82f6",
                                    textTransform: "none",
                                    border: "1px dashed #334155",
                                    py: 1,
                                    "&:hover": {
                                        bgcolor: "#334155",
                                        borderColor: "#3b82f6",
                                    },
                                }}
                            >
                                Create New Portfolio
                            </Button>
                        </>
                    ) : (
                        <Box>
                            <Typography fontSize="var(--font-size-xs2)" color="#94a3b8" mb={1.5}>
                                Enter a name for your new portfolio:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g., Growth Stocks"
                                value={newPortfolioName}
                                onChange={(e) => setNewPortfolioName(e.target.value)}
                                autoFocus
                                InputProps={{
                                    sx: {
                                        fontSize: 12,
                                        borderRadius: 1,
                                        bgcolor: "#0f172a",
                                        color: "#e2e8f0",
                                        '& input::placeholder': {
                                            color: '#64748b',
                                            opacity: 1
                                        },
                                        '& fieldset': {
                                            borderColor: "#334155",
                                        },
                                        '&:hover fieldset': {
                                            borderColor: "#475569",
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: "#3b82f6",
                                            borderWidth: "1px",
                                        },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 2.5, pb: 2, borderTop: "1px solid #334155" }}>
                    <Button
                        onClick={() => {
                            if (creatingPortfolio) {
                                setCreatingPortfolio(false);
                                setNewPortfolioName('');
                            } else {
                                setPortfolioDialogOpen(false);
                                setSelectedStock(null);
                                setSelectedPortfolioId("");
                            }
                        }}
                        disabled={addingToPortfolio}
                        sx={{
                            fontSize: 11.5,
                            fontWeight: 500,
                            color: "#94a3b8",
                            textTransform: "none",
                            "&:hover": {
                                bgcolor: "#334155",
                            },
                        }}
                    >
                        {creatingPortfolio ? 'Back' : 'Cancel'}
                    </Button>

                    {/* If Creating New Portfolio */}
                    {creatingPortfolio ? (
                        <Button
                            onClick={handleCreatePortfolio}
                            disabled={addingToPortfolio || !newPortfolioName.trim()}
                            sx={{
                                fontSize: 11.5,
                                fontWeight: 500,
                                bgcolor: "#3b82f6",
                                color: "#fff",
                                textTransform: "none",
                                px: 2,
                                "&:hover": {
                                    bgcolor: "#2563eb",
                                },
                                "&.Mui-disabled": {
                                    bgcolor: "#334155",
                                    color: "#64748b",
                                },
                            }}
                        >
                            {addingToPortfolio ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                "Create & Add"
                            )}
                        </Button>
                    ) : (
                        /* If Selecting Existing Portfolio */
                        <Button
                            onClick={() => handleAddToExistingPortfolio(selectedPortfolioId)}
                            disabled={addingToPortfolio || !selectedPortfolioId}
                            sx={{
                                fontSize: 11.5,
                                fontWeight: 500,
                                bgcolor: "#10b981",
                                color: "#fff",
                                textTransform: "none",
                                px: 2,
                                "&:hover": {
                                    bgcolor: "#059669",
                                },
                                "&.Mui-disabled": {
                                    bgcolor: "#334155",
                                    color: "#64748b",
                                },
                            }}
                        >
                            {addingToPortfolio ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                "Add"
                            )}
                        </Button>
                    )}
                </DialogActions>

            </Dialog>
        </Box>
    );
};

export default ETFsScreener;